import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Card from '../../common/element/Card';
import Heading from '../../common/element/Heading';
import BtnPrimary from '../../common/element/Buttons/btnPrimary';
import ThreeDotLoader from '../../common/element/ButtonLoader/ThreeDotLoader';
import { submitProspectCompany } from '../../redux/tool/toolSlice';
import { selectProspectSubmitLoading, selectProspectSubmitData, selectProspectSubmitError } from '../../redux/tool/toolSelectors';
import { selectCheckSessionUrlLoading } from '../../redux/SessionUrl/sessionUrlSelectors';
import { buildProspectPayload, getFormHeader } from '../../common/commonFunctions';
import CommonPhoneInput from '../../common/CommonPhoneInput/CommonPhoneInput';
import Select from '../../common/element/Select';
import { CgSpinner } from 'react-icons/cg';
import SweetAlertService from '../../common/SweetAlert/SweetAlert';
import { checkSessionUrl } from '../../redux/SessionUrl/sessionUrlSlice';
import { searchPublicCities, clearSearchCities } from '../../redux/PublicCity/publicCitySlice';
import { selectSearchCitiesData, selectSearchCitiesLoading } from '../../redux/PublicCity/publicCitySelectors';

const logoImage = '/assets/images/Logo-Incredible-Leaders.png';

const WORK_FIELD_OPTIONS = ['Business', 'Job', 'Student', 'Housewife'].map(
  (w) => ({ value: w, label: w })
);

// Own loading indicator for the city search (avoids the shared Spinner)
const CityLoadingIndicator = () => <CgSpinner className="animate-spin text-gray-400 mr-2" size={18} />;

const LoginFormFORprospect = ({
  headerOverrides = {},
  initialValues = {},
  onSuccess,
  sessionType = null,
  sessionName = null,
  companySessionId = null,
  showProspectFields = false,
}) => {
  const dispatch = useDispatch();
  const isSessionUrlFlow = sessionType === 'session_url';
  const prospectLoading = useSelector(selectProspectSubmitLoading);
  const checkLoading = useSelector(selectCheckSessionUrlLoading);
  const loading = isSessionUrlFlow ? checkLoading : prospectLoading;
  const response = useSelector(selectProspectSubmitData);
  const error = useSelector(selectProspectSubmitError);
  const citiesData = useSelector(selectSearchCitiesData);
  const citiesLoading = useSelector(selectSearchCitiesLoading);

  const header = getFormHeader(headerOverrides);

  // --- Form State ---
  const [companyName, setCompanyName] = useState(initialValues.company_name ?? initialValues.companyName ?? '');
  const [userName, setUserName] = useState(initialValues.user_name ?? initialValues.userName ?? '');
  const [email, setEmail] = useState(initialValues.email ?? '');
  const [phone, setPhone] = useState(initialValues.phone_number ?? initialValues.phoneNumber ?? '');
  const [phoneCountry, setPhoneCountry] = useState(initialValues.phone_country ?? initialValues.phoneCountry ?? null);
  const [city, setCity] = useState(
    initialValues.city ? { value: initialValues.city, label: initialValues.city, cityName: initialValues.city } : null
  );
  const [citySearch, setCitySearch] = useState('');
  const citySearchTimer = useRef(null);

  const cityOptions = (Array.isArray(citiesData) ? citiesData : []).map((item) => ({
    value: item.city_id,
    label: [item.city_name, item.state_name, item.country_name].filter(Boolean).join(', '),
    cityName: item.city_name,
  }));
  const [workField, setWorkField] = useState(
    WORK_FIELD_OPTIONS.find((o) => o.value === (initialValues.work_field ?? initialValues.workField)) || null
  );

  // --- Error State ---
  const [errors, setErrors] = useState({});

  // --- Validation Logic ---
  const validateForm = () => {
    let formIsValid = true;
    let newErrors = {};

    // 1. Validate Company Name
    if (!companyName || companyName.trim() === '') {
      formIsValid = false;
      newErrors.companyName = 'Company name is required';
    }

    // 2. Validate User Name
    if (!userName || userName.trim() === '') {
      formIsValid = false;
      newErrors.userName = 'Name is required';
    }

    // 3. Validate Email
    if (!email || email.trim() === '') {
      formIsValid = false;
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      formIsValid = false;
      newErrors.email = 'Please enter a valid email address';
    }

    // 4. Validate Phone
    if (!phone || String(phone).trim() === '') {
      formIsValid = false;
      newErrors.phone = 'Phone Number is required';
    } else if (!isPhoneValid(phone, phoneCountry)) {
      formIsValid = false;
      newErrors.phone = 'Please enter a valid phone number';
    }

    // 5. Validate City & Work Field (prospect only)
    if (showProspectFields) {
      if (!city) {
        formIsValid = false;
        newErrors.city = 'City is required';
      }
      if (!workField) {
        formIsValid = false;
        newErrors.workField = 'Work field is required';
      }
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const isPhoneValid = (value, country) => {
    if (!value) return true;
    if (country && country.format) {
      const requiredLength = (country.format.match(/\./g) || []).length;
      const digitsOnly = String(value).replace(/\D/g, '');
      return digitsOnly.length >= requiredLength;
    }
    return true;
  };

  const submitHandler = async (e) => {
    e && e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (isSessionUrlFlow) {
      try {
        const action = await dispatch(
          checkSessionUrl({
            company_session_id: Number(companySessionId),
            email,
          })
        );
        if (checkSessionUrl.fulfilled.match(action)) {
          if (onSuccess) {
            onSuccess({
              ...action.payload,
              userData: { name: userName, email, phone },
            });
          }
        } else if (checkSessionUrl.rejected.match(action)) {
          SweetAlertService.showToast({
            icon: 'error',
            title: action.payload || 'Failed to verify session. Please try again.',
          });
        }
      } catch (err) {
        // handled by redux
      }
      return;
    }

    const payload = buildProspectPayload({
      company_id: initialValues.company_id ?? 0,
      company_name: companyName,
      user_name: userName,
      email,
      phone_number: phone,
      ...(showProspectFields && { city: city.cityName, work_field: workField.value })
    });

    try {
      const action = await dispatch(submitProspectCompany({ payload }));
      if (submitProspectCompany.fulfilled.match(action)) {
        const returnedToken = action.payload?.token || action.payload?.data?.token || null;
        if (returnedToken) {
          try {
            localStorage.setItem('prospect_token', returnedToken);
          } catch (err) {
            // ignore localStorage errors (e.g., private mode)
          }
        }

        if (onSuccess) onSuccess(action.payload);
      }
    } catch (e) {
      // handled by redux
    }
  };

  // Debounced city search (GET /public/cities/search?search=)
  const handleCitySearch = (inputValue, { action }) => {
    if (action !== 'input-change') {
      // clear typed text once a city is picked or the field loses focus
      if (action === 'set-value' || action === 'input-blur') setCitySearch('');
      return;
    }
    setCitySearch(inputValue);
    clearTimeout(citySearchTimer.current);
    if (!inputValue.trim()) {
      dispatch(clearSearchCities());
      return;
    }
    citySearchTimer.current = setTimeout(() => {
      dispatch(searchPublicCities(inputValue.trim()));
    }, 400);
  };

  useEffect(() => () => clearTimeout(citySearchTimer.current), []);

  // Helper to remove error when user types
  const handleInputChange = (setter, field, value) => {
    setter(value);
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  // (Phone input handled via CommonPhoneInput component)

  const handlePhoneChange = (value, country) => {
    // value: phone string from the phone input (may include + and country code)
    setPhone(value);
    setPhoneCountry(country || null);

    // clear or set error based on length for the selected country
    if (!value) {
      setErrors(prev => ({ ...prev, phone: null }));
      return;
    }

    if (country && country.format) {
      const requiredLength = (country.format.match(/\./g) || []).length;
      const digitsOnly = String(value).replace(/\D/g, '');
      if (digitsOnly.length < requiredLength) {
        setErrors(prev => ({ ...prev, phone: 'Please enter a valid phone number' }));
      } else {
        setErrors(prev => ({ ...prev, phone: null }));
      }
    } else {
      setErrors(prev => ({ ...prev, phone: null }));
    }
  };

  // Format various error shapes into a user-friendly string
  const formatError = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (Array.isArray(err)) return err.join(', ');
    if (err.message) return err.message;
    if (err.data && err.data.message) return err.data.message;

    // If there are field-level errors like { errors: { email: ['..'] } }
    if (err.errors && typeof err.errors === 'object') {
      const parts = [];
      Object.values(err.errors).forEach((v) => {
        if (Array.isArray(v)) parts.push(...v);
        else if (v) parts.push(String(v));
      });
      if (parts.length) return parts.join(', ');
    }

    // Fall back to stringifying the object (shortened)
    try {
      const json = JSON.stringify(err);
      return json.length > 300 ? json.slice(0, 300) + '...' : json;
    } catch (e) {
      return String(err);
    }
  };

  // Show toast when redux error changes
  useEffect(() => {
    if (error) {
      SweetAlertService.showToast({ icon: 'error', title: formatError(error) });
    }
  }, [error]);

  return (
    <div style={{ backgroundColor: '#FFF4E5' }} className="flex justify-center min-h-screen">
      <div className="min-h-screen justify-center flex py-12 px-6">
        <div className="flex-col items-center justify-center flex">
          <div className="flex items-center mb-6">
            <img className="brand-img" src={logoImage} alt="conserve" />
          </div>

          <Heading className="text-center mb-3">{header.title}</Heading>
          <p className="font-lato font-medium text-base leading-[1.5] text-center text-[#475467] mb-[28px]">
            {header.subtitle}
          </p>

          <Card className="bg-white w-500 rounded-xl block-shadow w-100 mt-2 lg:px-10 lg:py-8 px-6 py-6">
            <div className="w-full">
              <form onSubmit={submitHandler}>
                
                {/* Company Name Field */}
                <div className="mb-5">
                  <label className="block mb-[10px] font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] text-start">
                    Company Name
                  </label>
                  <div className="input-wrapper w-full flex-col">
                    <input
                      className={`input-shadow h-10 text-sm rounded-lg capitalize appearance-none border w-full py-2 px-3 pr-8 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                        errors.companyName ? 'border-red-500' : 'border-gray-300'
                      } ${
                        (initialValues.company_name || initialValues.companyName) ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      type="text"
                      placeholder="TechNova Solutions"
                      value={companyName}
                      onChange={(e) => handleInputChange(setCompanyName, 'companyName', e.target.value)}
                      disabled={!!(initialValues.company_name || initialValues.companyName)}
                      readOnly={!!(initialValues.company_name || initialValues.companyName)}
                    />
                    {errors.companyName && (
                      <p className="text-red-500 text-sm mt-1 text-start">{errors.companyName}</p>
                    )}
                  </div>
                </div>

                {/* Session Name Field — shown only for session_url flow */}
                {isSessionUrlFlow && sessionName && (
                  <div className="mb-5">
                    <label className="block mb-[10px] font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] text-start">
                      Session Name
                    </label>
                    <div className="input-wrapper w-full flex-col">
                      <input
                        className="input-shadow h-10 text-sm rounded-lg appearance-none border border-gray-300 w-full py-2 px-3 pr-8 text-gray-700 leading-tight bg-gray-100 cursor-not-allowed"
                        type="text"
                        value={sessionName}
                        disabled
                        readOnly
                      />
                    </div>
                  </div>
                )}

                {/* Name Field */}
                <div className="mb-5">
                  <label className="block mb-[10px] font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] text-start">
                    Name*
                  </label>
                  <div className="input-wrapper w-full flex-col">
                    <input
                      className={`input-shadow h-10 text-sm rounded-lg appearance-none border w-full py-2 px-3 pr-8 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                        errors.userName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      type="text"
                      placeholder="Enter your name"
                      value={userName}
                      onChange={(e) => handleInputChange(setUserName, 'userName', e.target.value)}
                    />
                    {errors.userName && (
                      <p className="text-red-500 text-sm mt-1 text-start">{errors.userName}</p>
                    )}
                  </div>
                </div>

                {/* Email Field */}
                <div className="mb-5">
                  <label className="block mb-[10px] font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] text-start">
                    Email*
                  </label>
                  <div className="input-wrapper w-full flex-col">
                    <input
                      className={`input-shadow h-10 text-sm rounded-lg appearance-none border w-full py-2 px-3 pr-8 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => handleInputChange(setEmail, 'email', e.target.value)}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1 text-start">{errors.email}</p>
                    )}
                  </div>
                </div>

                {/* Phone Field - UPDATED (uses CommonPhoneInput) */}
                <div className={showProspectFields ? 'mb-5' : 'mb-6'}>
                  <label className="block mb-[10px] font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] text-start">
                    Phone Number*
                  </label>
                  <div className="input-wrapper w-full flex-col">
                    <CommonPhoneInput
                      value={phone}
                      onChange={handlePhoneChange}
                      onBlur={() => { /* optional: trigger blur validation if needed */ }}
                      placeholder="Enter your phone number"
                      country="in"
                      error={errors.phone}
                      disabled={false}
                    />
                  </div>
                </div>

                {showProspectFields && (
                  <>
                    {/* City Field */}
                    <div className="mb-5">
                      <label className="block mb-[10px] font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] text-start">
                        City*
                      </label>
                      <div className="input-wrapper w-full flex-col">
                        <Select
                          className="text-start"
                          options={cityOptions}
                          value={city}
                          onChange={(option) => handleInputChange(setCity, 'city', option)}
                          inputValue={citySearch}
                          handleSearchChange={handleCitySearch}
                          filterOption={null}
                          isLoading={citiesLoading}
                          components={{ LoadingIndicator: CityLoadingIndicator }}
                          placeholder="Search your city"
                          noOptionsMessage={() => (citySearch.trim() ? 'No cities found' : 'Type to search city')}
                        />
                        {errors.city && (
                          <p className="text-red-500 text-sm mt-1 text-start">{errors.city}</p>
                        )}
                      </div>
                    </div>

                    {/* Work Field */}
                    <div className="mb-6">
                      <label className="block mb-[10px] font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] text-start">
                        Work Field*
                      </label>
                      <div className="input-wrapper w-full flex-col">
                        <Select
                          className="text-start"
                          options={WORK_FIELD_OPTIONS}
                          value={workField}
                          onChange={(option) => handleInputChange(setWorkField, 'workField', option)}
                          placeholder="Select your work field"
                        />
                        {errors.workField && (
                          <p className="text-red-500 text-sm mt-1 text-start">{errors.workField}</p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                <div className="flex items-center justify-between w-100">
                  <BtnPrimary disabled={loading} type="submit" className="w-full">
                    {loading ? <ThreeDotLoader /> : 'Next'}
                  </BtnPrimary>
                </div>

                {response && (
                  <div className="text-sm text-green-600 mt-3">Submission successful.</div>
                )}
                {/* error shown via toast */}
              </form>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginFormFORprospect;